import update from "immutability-helper";
import { partition } from "lodash-es";

import { asTuple } from "utilities/array/array";

import { isLineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import {
    findCategoryById,
    getFormatGroupsFromFormatItems,
    isProposalSubCategory,
    toRootCategory,
    toSubCategory,
} from "commonComponents/utilities/ProposalBaseLineItemContainer/WorksheetLineItemContainer.utility";

import {
    type FormatItem,
    isProposalCategory,
    isUnassignedCategory,
    type ProposalCategory,
    type ProposalSubCategory,
    UnassignedCostGroupId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import { getUnassignedCategory } from "entity/estimate/Proposal/Proposal.types";

/**
 * Ensure only line items are selected
 */
export const unselectGroups = <T extends FormatItem>(formatItems: T[]): T[] => {
    return formatItems.map((formatItem) => {
        if (isProposalCategory(formatItem) && (formatItem.isSelected || formatItem.items)) {
            const category: ProposalCategory = formatItem;

            return update(category, {
                isSelected: { $set: false },
                items: { $set: unselectGroups(formatItem.items) },
            }) as T;
        }
        return formatItem;
    });
};

/**
 * Assumes if something is selected, so are all children
 */
export const pruneSelectedBranchesFromListOfFormatItems = <T extends FormatItem>(
    rootNodes: T[],
) => {
    const prunedSelectedNodes: FormatItem[] = [];
    const prunedUnselectedNodes: T[] = [];

    rootNodes.forEach((node) => {
        if (node.isSelected) {
            prunedSelectedNodes.push(node);
        } else {
            if (isProposalSubCategory(node)) {
                const subCategory: ProposalSubCategory = node;
                const [childSelectedNodes, childUnselectedNodes] =
                    pruneSelectedBranchesFromListOfFormatItems(subCategory.items);

                prunedSelectedNodes.push(...childSelectedNodes);

                if (childSelectedNodes.length) {
                    prunedUnselectedNodes.push(
                        update(subCategory, {
                            items: { $set: childUnselectedNodes },
                        }) as T,
                    );
                    return;
                }
            } else if (isProposalCategory(node)) {
                const category: ProposalCategory = node;
                const [childSelectedNodes, childUnselectedNodes] =
                    pruneSelectedBranchesFromListOfFormatItems(category.items);

                prunedSelectedNodes.push(...childSelectedNodes);

                if (childSelectedNodes.length) {
                    prunedUnselectedNodes.push(
                        update(category, {
                            items: { $set: childUnselectedNodes },
                        }) as T,
                    );
                    return;
                }
            }

            prunedUnselectedNodes.push(node);
        }
    });

    return asTuple(prunedSelectedNodes, prunedUnselectedNodes);
};

export const unselectFormatItemAndChildren = <T extends FormatItem>(rootNode: T): T => {
    if (isProposalCategory(rootNode)) {
        const categoryNode: ProposalCategory = rootNode;
        const newItems = categoryNode.items.map((item) => unselectFormatItemAndChildren(item));

        return update(categoryNode, {
            isSelected: { $set: false },
            items: { $set: newItems },
        }) as T;
    } else {
        const lineItemNode: WorksheetLineItem = rootNode;
        return update(lineItemNode, { isSelected: { $set: false } }) as T;
    }
};

const updateMovingFormatItems = (formatItems: FormatItem[], destinationCategoryId: number) => {
    return formatItems.map((item) => {
        if (isProposalSubCategory(item)) {
            // Is a subcategory
            if (destinationCategoryId === UnassignedCostGroupId) {
                // Make Unassigned Cost Group a root category
                return toRootCategory(item);
            } else if (destinationCategoryId !== item.parentId) {
                // Subcategory moved to a differnt root category
                return update(item, {
                    parentId: { $set: destinationCategoryId },
                    _isDirty: { $set: true },
                });
            }
        } else if (isProposalCategory(item)) {
            // Is a root category
            if (destinationCategoryId !== UnassignedCostGroupId) {
                // The root category is being made into a subcategory
                return toSubCategory(item, destinationCategoryId);
            }
        } else if (isLineItemRow(item)) {
            return update(item, {
                costCategoryFormatId: { $set: destinationCategoryId },
                _isDirty: { $set: true },
            });
        }

        return item;
    });
};

/**
 * Assumes Unassigned Category is at the end of currentMutableHierarchy
 */
const updateUnassignedCategoryAsDestination = (
    currentMutableHierarchy: ProposalCategory[],
    currentUnassignedCategory: ProposalCategory,
    movingRootCategories: ProposalCategory[],
    movingLineItems: WorksheetLineItem[],
    destinationPosition?: number,
) => {
    if (movingRootCategories.length > 0) {
        currentMutableHierarchy.splice(
            destinationPosition ?? currentMutableHierarchy.length - 1,
            0,
            ...movingRootCategories,
        );
    }

    if (movingLineItems.length > 0) {
        return update(currentUnassignedCategory, {
            items: {
                $splice: [
                    [
                        destinationPosition ?? currentUnassignedCategory.items.length,
                        0,
                        ...movingLineItems,
                    ],
                ],
            },
        });
    }

    return currentUnassignedCategory;
};

/**
 * Insert items in default positions
 * Category contents consists of all line items followed by all subgroups
 *
 * When inserting new items, put them at the bottom of their section
 */
export const updateCategoryUsingDefaultPositioning = (
    currentDestination: ProposalCategory,
    movingSubCategories: ProposalSubCategory[],
    movingLineItems: WorksheetLineItem[],
) => {
    let indexAfterLineItems = currentDestination.items.findIndex(isProposalCategory);
    if (indexAfterLineItems === -1) {
        indexAfterLineItems = currentDestination.items.length;
    }

    return update(currentDestination, {
        items: {
            $splice: [
                [currentDestination.items.length, 0, ...movingSubCategories],
                [indexAfterLineItems, 0, ...movingLineItems],
            ],
        },
    });
};

const UpdateHierarchyWithUpdatedDestination = (
    currentMutableHierarchy: ProposalCategory[],
    updatedDestination: ProposalCategory,
) => {
    if (updatedDestination.id === UnassignedCostGroupId) {
        currentMutableHierarchy[currentMutableHierarchy.length - 1] = updatedDestination;
    } else if (isProposalSubCategory(updatedDestination)) {
        const destinationAsSubCategory: ProposalSubCategory = updatedDestination;

        const parentOfDestinationIndex = currentMutableHierarchy.findIndex(
            (item) => item.id === destinationAsSubCategory.parentId,
        );
        const parentOfDestination = currentMutableHierarchy[parentOfDestinationIndex];
        const newParentItems = parentOfDestination.items.map((item) =>
            item.id === destinationAsSubCategory.id ? destinationAsSubCategory : item,
        );

        currentMutableHierarchy[parentOfDestinationIndex] = update(parentOfDestination, {
            items: {
                $set: newParentItems,
            },
        });
    } else {
        const destinationIndex = currentMutableHierarchy.findIndex(
            (item) => item.id === updatedDestination?.id,
        );

        currentMutableHierarchy[destinationIndex] = updatedDestination;
    }
    updatedDestination._isDirty = true;

    updateDisplayOrderInFormatItems(currentMutableHierarchy);
    return currentMutableHierarchy;
};

const updateDisplayOrderInFormatItems = (formatItems: FormatItem[]) => {
    formatItems.forEach((fi, index) => {
        const newDisplayOrder = index + 1; // display order is 1-indexed
        if (fi.displayOrder !== newDisplayOrder && fi.id !== UnassignedCostGroupId) {
            fi._isDirty = true;
            fi.displayOrder = newDisplayOrder;
        }
        if (isProposalCategory(fi) || isProposalSubCategory(fi)) {
            updateDisplayOrderInFormatItems(fi.items);
        }
    });
};

/**
 * Method for handling the insertion and adjustment of existing format items that
 * are in the process of being moved. Meant to be used for all move actions including
 * from checked actions and when using drag and drop. Assumes that the moving items
 * have already been removed from the list of unmoving categories.
 *
 * @param destinationPosition the index within the destination to insert
 * the items at. Omitting it will move the items to a default position instead.
 */
export const insertFormatItemsIntoList = (
    builderId: number,
    unmovingFormatItems: ProposalCategory[],
    movingFormatItems: FormatItem[],
    destinationCategoryId: number,
    destinationPosition?: number,
) => {
    const allUnmovingCategories = getFormatGroupsFromFormatItems(unmovingFormatItems, true);

    let destination = allUnmovingCategories.find(
        (category) => category.id === destinationCategoryId,
    );

    if (!destination) {
        // The user may select the unassigned category even though it may not exist yet on the format
        // Create it if so
        if (destinationCategoryId === UnassignedCostGroupId) {
            destination = getUnassignedCategory(builderId);
            unmovingFormatItems.push(destination);
        } else {
            // This shouldn't happen
            return undefined;
        }
    }

    const updatedMovingFormatItems = updateMovingFormatItems(
        movingFormatItems,
        destinationCategoryId,
    );

    const newFormatItems = [...unmovingFormatItems];
    const [movingRootAndSubCategories, movingLineItems] = partition(
        updatedMovingFormatItems,
        isProposalCategory,
    );
    const [movingSubCategories, movingRootCategories] = partition(
        movingRootAndSubCategories,
        isProposalSubCategory,
    );

    // add the moving items to the destination
    if (destination.id === UnassignedCostGroupId) {
        destination = updateUnassignedCategoryAsDestination(
            newFormatItems,
            destination,
            movingRootCategories,
            movingLineItems,
            destinationPosition,
        );
    } else if (destinationPosition !== undefined) {
        // If moving a subgroup, make sure to insert it after the group's line items.
        if (movingSubCategories.length === 1 && movingLineItems.length === 0) {
            const firstSubgroupInGroupIndex = destination.items.findIndex(isProposalSubCategory);
            if (destinationPosition < firstSubgroupInGroupIndex) {
                destinationPosition = firstSubgroupInGroupIndex;
            }
        }
        destination = update(destination, {
            items: {
                $splice: [[destinationPosition, 0, ...movingSubCategories, ...movingLineItems]],
            },
        });
    } else {
        destination = updateCategoryUsingDefaultPositioning(
            destination,
            movingSubCategories,
            movingLineItems,
        );
    }

    return UpdateHierarchyWithUpdatedDestination(newFormatItems, destination);
};

/**
 * Inserts an unsaved format item (negative id) at the top of its destination, so the worksheet can
 * render it as a real row instead of tracking a separate "pending" row. A top-level group goes to
 * the front of the list; anything else to the front of `parentGroupId`'s children.
 *
 * Returns the list unchanged when the destination can't be found or the item can't live there, so a
 * bad parent id drops the row rather than misplacing it.
 */
export const insertNewFormatItemAtTop = (
    formatItems: ProposalCategory[],
    newItem: FormatItem,
    parentGroupId: number | null,
): ProposalCategory[] => {
    const isTopLevelGroup = isProposalCategory(newItem) && !isProposalSubCategory(newItem);

    if (parentGroupId == null) {
        // Only a top-level group can live at the root of the worksheet.
        return isTopLevelGroup ? [newItem, ...formatItems] : formatItems;
    }
    // A group's children are line items and sub-groups only, so a top-level group can't nest.
    if (isTopLevelGroup) {
        return formatItems;
    }

    const insertIntoCategory = <T extends ProposalCategory>(category: T): T => {
        // Sub-groups sort after a group's line items, so each kind goes to the top of its own section
        // rather than blindly at index 0 — a sub-group added to a group with no other sub-groups
        // therefore lands at the end, after the line items.
        const firstSubGroupIndex = category.items.findIndex(isProposalCategory);
        const insertAt = !isProposalSubCategory(newItem)
            ? 0
            : firstSubGroupIndex === -1
              ? category.items.length
              : firstSubGroupIndex;
        const items = [...category.items];
        items.splice(insertAt, 0, newItem);
        return { ...category, items };
    };

    return formatItems.map((category) => {
        if (category.id === parentGroupId) {
            return insertIntoCategory(category);
        }
        if (!getSubCategories(category).some((sub) => sub.id === parentGroupId)) {
            return category;
        }
        return {
            ...category,
            items: category.items.map((item) =>
                isProposalSubCategory(item) && item.id === parentGroupId
                    ? insertIntoCategory(item)
                    : item,
            ),
        };
    });
};

/**
 * Removes an unsaved format item by id from anywhere in the hierarchy — the undo for
 * {@link insertNewFormatItemAtTop} when the user abandons a new row.
 */
export const removeFormatItemById = (
    formatItems: ProposalCategory[],
    itemId: number,
): ProposalCategory[] =>
    formatItems
        .filter((category) => category.id !== itemId)
        .map((category) => ({
            ...category,
            items: category.items
                .filter((item) => item.id !== itemId)
                .map((item) =>
                    isProposalSubCategory(item)
                        ? {
                              ...item,
                              items: item.items.filter((child) => child.id !== itemId),
                          }
                        : item,
                ),
        }));

/**
 * The saved line items a new row is ordered against. Unassigned is a client-only grouping, not a
 * real category, so `findCategoryById` can't resolve its id (-1) and it's located by identity
 * instead. `excludeId` drops the unsaved row whose order is being computed.
 */
export const getLineItemSiblings = (
    formatItems: ProposalCategory[],
    parentGroupId: number,
    excludeId: number,
): WorksheetLineItem[] => {
    const parentGroup =
        parentGroupId === UnassignedCostGroupId
            ? formatItems.find(isUnassignedCategory)
            : findCategoryById(parentGroupId, formatItems);
    return (parentGroup?.items.filter(isLineItemRow) ?? []).filter((li) => li.id !== excludeId);
};

/**
 * Display order placing a new row above its siblings: the current minimum, which the backend
 * vacates by shifting the existing rows down. Using the min rather than min − 1 keeps the value
 * inside the existing range, so it can't go negative on a 0-based bucket.
 */
export const getTopDisplayOrder = (
    siblings: readonly { displayOrder?: number | null }[],
): number => {
    const orders = siblings.map((s) => s.displayOrder).filter((o): o is number => o != null);
    return orders.length > 0 ? Math.min(...orders) : 0;
};

/**
 * Return sub categories of the passed in proposal category
 */
export function getSubCategories(category: ProposalCategory): ProposalSubCategory[] {
    return category.items.filter(isProposalSubCategory);
}

/**
 * Return whether or not the item (represented by id) is a child of a sub category
 */
export function isChildOfSubCategory(formatItems: FormatItem[], itemId: number): boolean {
    for (const formatItem of formatItems) {
        if (isProposalCategory(formatItem)) {
            if (formatItem.id === itemId) {
                return false;
            }

            for (const formatItemChild of formatItem.items) {
                if (formatItemChild.id === itemId) {
                    return false;
                }

                if (isProposalSubCategory(formatItemChild)) {
                    for (const subCategoryChild of formatItemChild.items) {
                        if (subCategoryChild.id === itemId) {
                            return true;
                        }
                    }
                }
            }
        }
    }
    return false;
}
